import { WebAuth, type Auth0Error, type CrossOriginLoginOptions } from 'auth0-js';
import { authConfig, crossOriginVerificationPath } from './config';

export class EmbeddedLoginError extends Error {
  readonly code: string;
  constructor(code: string) {
    super('Embedded sign-in failed');
    this.code = code;
  }
}

function createWebAuth() {
  return new WebAuth({
    domain: authConfig.domain,
    clientID: authConfig.clientId,
    responseType: 'code',
    responseMode: 'query',
    redirectUri: window.location.origin,
  });
}

/** Keep the React SDK's PKCE transaction and let Auth0.js authenticate the credentials. */
export function signInWithCredentials(
  authorizationUrl: string,
  email: string,
  password: string,
): Promise<void> {
  const url = new URL(authorizationUrl);
  const params = url.searchParams;
  if (url.origin !== `https://${authConfig.domain}` || url.pathname !== '/authorize'
    || params.get('client_id') !== authConfig.clientId
    || params.get('response_type') !== 'code'
    || params.get('code_challenge_method') !== 'S256'
    || !params.get('code_challenge') || !params.get('state') || !params.get('nonce')
    || params.get('redirect_uri') !== window.location.origin) {
    return Promise.reject(new EmbeddedLoginError('invalid_authorization_request'));
  }

  const options: CrossOriginLoginOptions & Record<string, unknown> = {
    username: email.trim(),
    password,
    realm: authConfig.connection,
    clientID: authConfig.clientId,
    responseType: 'code',
    responseMode: 'query',
    redirectUri: params.get('redirect_uri')!,
    state: params.get('state')!,
    nonce: params.get('nonce')!,
    scope: params.get('scope') || 'openid profile email',
    audience: params.get('audience') || authConfig.audience,
    code_challenge: params.get('code_challenge')!,
    code_challenge_method: 'S256',
  };
  for (const key of ['organization', 'invitation', 'ui_locales', 'acr_values']) {
    const value = params.get(key);
    if (value) options[key] = value;
  }

  return new Promise((resolve, reject) => {
    options.onRedirecting = (done) => { resolve(); done(); };
    createWebAuth().login(options, (error: Auth0Error | null) => {
      reject(new EmbeddedLoginError(error?.code || error?.error || 'request_error'));
    });
  });
}

export function sendPasswordReset(email: string, connection = authConfig.connection): Promise<void> {
  return new Promise((resolve, reject) => {
    createWebAuth().changePassword({
      connection,
      email: email.trim(),
    }, (error) => {
      if (error) reject(new EmbeddedLoginError(error.code || error.error || 'request_error'));
      else resolve();
    });
  });
}

export function runCrossOriginVerification() {
  if (window.location.pathname === crossOriginVerificationPath) {
    createWebAuth().crossOriginVerification();
    return true;
  }
  return false;
}

export function loginErrorMessage(error: unknown): string {
  if (!(error instanceof EmbeddedLoginError)) return 'Unable to sign in. Please try again.';
  switch (error.code) {
    case 'invalid_user_password':
      return 'Email or password is incorrect. Please try again.';
    case 'access_denied':
      return 'Unable to sign in. Please try again.';
    case 'too_many_attempts': case 'too_many_requests':
      return 'Too many sign-in attempts. Please wait before trying again.';
    case 'password_leaked':
      return 'Please reset your password before signing in.';
    case 'unauthorized_client': case 'invalid_request':
      return 'Email and password sign-in is not available yet. Please try again later.';
    default:
      return 'Unable to sign in. Please try again.';
  }
}
